import React, { useState, useEffect } from 'react';
import { 
  Navigation, 
  Shield, 
  Zap, 
  Target, 
  Users, 
  Award, 
  TrendingUp, 
  MapPin, 
  Clock, 
  CheckCircle,
  ArrowRight,
  Globe,
  BarChart3,
  Smartphone,
  Car,
  Star,
  Play,
  Route,
  Gauge,
  Bell,
  Lock,
  Activity,
  Download,
  ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import MainNavbar from '../../components/layout/MainNavbar';

const Home = () => {
  const navigate = useNavigate();
  const [theme, setTheme] = useState('light');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activeFeature, setActiveFeature] = useState(0);

  // Theme management
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  // Mouse move effect for animated background
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const stats = [
    { icon: Users, value: '50K+', label: 'Active Users', color: 'from-blue-500 to-cyan-500' },
    { icon: Car, value: '100K+', label: 'Vehicles Tracked', color: 'from-purple-500 to-pink-500' },
    { icon: Globe, value: '150+', label: 'Countries', color: 'from-green-500 to-emerald-500' },
    { icon: Award, value: '99.9%', label: 'Uptime', color: 'from-orange-500 to-red-500' },
  ];

  const features = [
    {
      icon: MapPin,
      title: 'Real-Time GPS Tracking',
      description: 'Track your entire fleet in real-time with pinpoint accuracy. Get instant location updates, speed monitoring, and route history.',
      color: 'from-blue-500 to-cyan-500',
      path: '/gps-tracker',
      image: 'https://images.unsplash.com/photo-1603638725135-928baf863eff?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTB8fGdwcyUyMHRyYWNraW5nfGVufDB8fDB8fHww'
    },
    {
      icon: BarChart3,
      title: 'Advanced Analytics',
      description: 'Make data-driven decisions with comprehensive reports, fuel consumption analysis, driver behavior insights, and performance metrics.',
      color: 'from-purple-500 to-pink-500',
      path: '/analytics',
      image: 'https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTJ8fEFuYWx5c2lzfGVufDB8fDB8fHww'
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Military-grade encryption protects your fleet data. Multi-level access control ensures only authorized personnel can view sensitive information.',
      color: 'from-green-500 to-emerald-500',
      path: '/security',
      image: '/icons/enterprise-security.jpg'
    },
    {
      icon: Smartphone,
      title: 'Mobile Management',
      description: 'Control your fleet from anywhere with our intuitive mobile app. Get alerts, view reports, and manage vehicles on-the-go.',
      color: 'from-orange-500 to-red-500',
      path: '/mobile-management',
      image: 'https://images.unsplash.com/photo-1731082154898-2e63df0f2a42?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8bW9iaWxlJTIwbWFwfGVufDB8fDB8fHww'
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Reduce Costs',
      description: 'Optimize routes and reduce fuel consumption by up to 30%',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Clock,
      title: 'Save Time',
      description: 'Automate fleet management tasks and save hours every day',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Shield,
      title: 'Enhance Safety',
      description: 'Monitor driver behavior and prevent accidents with real-time alerts',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Gauge,
      title: 'Boost Efficiency',
      description: 'Improve fleet utilization and reduce idle time significantly',
      color: 'from-orange-500 to-red-500'
    },
  ];

  const devices = [
    {
      name: 'REV',
      description: 'REV is a state-of-the-art GPS tracker designed for seamless real-time location tracking. Equipped with 4G LTE connectivity, it ensures reliable and swift data transmission, making it ideal for monitoring vehicles and assets. This compact device offers features such as geofencing, historical data storage, and customizable alerts, ensuring you stay informed and in control. Whether for personal use or fleet management, the REV provides peace of mind with its robust performance and easy installation.',
      image: '2.png',
      gradient: 'from-blue-500 to-cyan-500',
      learnMorePath: '/devices/rev'
    },
    {
      name: 'Wink',
      description: 'Wink is a state-of-the-art GPS tracker designed for seamless real-time location tracking. Equipped with 4G LTE connectivity, it ensures reliable and swift data transmission, making it ideal for monitoring vehicles and assets. This compact device offers features such as geofencing, historical data storage, and customizable alerts, ensuring you stay informed and in control. Whether for personal use or fleet management, the Wink provides peace of mind with its robust performance and easy installation.',
      image: '1.png',
      learnMorePath: '/devices/wink'
    }
  ];

  const testimonials = [
    {
      name: 'John Smith',
      role: 'Fleet Manager, Logistics Corp',
      content: 'TrackYourWheelz transformed our operations. We reduced fuel costs by 25% and improved driver safety significantly.',
      rating: 5
    },
    {
      name: 'Sarah Johnson',
      role: 'Operations Director, Transport Plus',
      content: 'The real-time tracking and analytics are game-changers. Our fleet efficiency has improved dramatically.',
      rating: 5
    },
    {
      name: 'Michael Chen',
      role: 'CEO, Global Logistics',
      content: 'Best investment we made. The ROI was visible within the first month. Highly recommended!',
      rating: 5
    },
  ];

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        @keyframes shimmer {
          0% {
            background-position: -1000px 0;
          }
          100% {
            background-position: 1000px 0;
          }
        }
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(139, 92, 246, 0.5);
          }
          50% {
            box-shadow: 0 0 40px rgba(139, 92, 246, 0.8);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.8s ease-out;
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out;
        }
        .animate-fade-in-left {
          animation: fadeInLeft 0.8s ease-out;
        }
        .animate-fade-in-right {
          animation: fadeInRight 0.8s ease-out;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
          background-size: 1000px 100%;
          animation: shimmer 3s infinite;
        }
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
        .glass {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        .glass-dark {
          background: rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
        .gradient-text {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .animate-on-scroll {
          opacity: 0;
        }
        .animate-on-scroll.animate-fade-in-up {
          opacity: 1;
        }
        .feature-card {
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .feature-card.active {
          transform: scale(1.05);
        }
      `}</style>

      <div className={`min-h-screen relative overflow-hidden transition-all duration-500 ${
        theme === 'dark' 
          ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e]' 
          : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'
      }`}>
      <MainNavbar theme={theme} toggleTheme={toggleTheme} activePage="Home" />
        
        {/* Background Elements */}
        <div className="absolute inset-0">
          {theme === 'dark' ? (
            // Dark theme background - Purple to Dark gradient (left to right)
            <>
              <div className="absolute inset-0 bg-gradient-to-r from-[#483D8B] to-[#2d1b4e]"></div>
            </>
          ) : (
            // Light theme background
            <>
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"></div>
              <div className="absolute top-20 left-20 w-96 h-96 bg-blue-200/30 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-200/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-indigo-200/30 rounded-full blur-3xl animate-pulse delay-500"></div>
            </>
          )}
        </div>

        {/* Hero Section */}
        <section className="relative z-10 pt-24 pb-16 lg:pt-32 lg:pb-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left Side - Content */}
              <div className="text-center lg:text-left space-y-6 lg:space-y-8 animate-fade-in-left">
                <div className="inline-block">
                  <span className={`px-5 py-2.5 rounded-full border text-sm md:text-base font-medium transition-all duration-500 ${
                    theme === 'dark'
                      ? 'bg-purple-500/20 border-purple-500/30 text-purple-300'
                      : 'bg-purple-100 border-purple-200 text-purple-700'
                  }`}>
                    🚀 #1 Fleet Management Platform
                  </span>
                </div>
                
                <div className="space-y-4">
                  <h1 className={`text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-black leading-tight ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Track Your Fleet
                    <span className="block gradient-text mt-2">Like Never Before</span>
                  </h1>
                  
                  <p className={`max-w-xl mx-auto lg:mx-0 text-sm md:text-base lg:text-lg leading-relaxed ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    Real-time GPS tracking, advanced analytics, and intelligent fleet management for modern businesses.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-2">
                  <Button
                    onClick={() => navigate('/login')}
                    className={`h-12 md:h-14 px-8 md:px-10 text-base md:text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-xl ${
                      theme === 'dark'
                        ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] hover:from-[#5a4da3] hover:to-[#3d2f6e] text-white'
                        : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
                    }`}
                  >
                    Get Started Free
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const featuresSection = document.getElementById('features');
                      featuresSection?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className={`h-12 md:h-14 px-8 md:px-10 text-base md:text-lg font-semibold transition-all duration-300 hover:scale-105 ${
                      theme === 'dark'
                        ? 'border-white/20 hover:bg-white/10 text-white'
                        : 'border-gray-300 hover:bg-gray-100 text-gray-900'
                    }`}
                  >
                    <Play className="mr-2 h-5 w-5" />
                    Watch Demo
                  </Button>
                </div>

                {/* Trust Indicators */}
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 lg:gap-6 pt-4">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className={`h-5 w-5 ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`} />
                    <span className={`text-sm md:text-base ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      No Credit Card Required
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className={`h-5 w-5 ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`} />
                    <span className={`text-sm md:text-base ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      14-Day Free Trial
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className={`h-5 w-5 ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`} />
                    <span className={`text-sm md:text-base ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Cancel Anytime
                    </span>
                  </div>
                </div>
              </div>

              {/* Right Side - Visual */}
              <div className="relative animate-fade-in-right max-w-md mx-auto lg:max-w-none">
                <div className={`relative ${theme === 'dark' ? 'glass-dark' : 'glass'} rounded-3xl p-6 md:p-8 lg:p-10 shadow-2xl animate-pulse-glow`}>
                  <div className="aspect-video lg:aspect-[16/9] rounded-2xl overflow-hidden bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center relative">
                    <div className="text-center space-y-4 z-10">
                      <div className={`w-12 h-12 md:w-32 md:h-32 lg:w-20 lg:h-10 mx-auto rounded-3xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center animate-float shadow-2xl`}>
                        <Car className="w-8 h-8 md:w-14 md:h-14 lg:w-10 lg:h-10 text-white" />
                      </div>
                      <p className={`text-sm md:text-sm lg:text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        Live Fleet Dashboard
                      </p>
                      <p className={`text-sm md:text-base ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Monitor your entire fleet in real-time
                      </p>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />
                  </div>
                  
                  
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {stats.map((stat, index) => (
                <Card
                  key={index}
                  className={`animate-on-scroll ${theme === 'dark' ? 'glass-dark' : 'glass'} border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className={`text-sm md:text-3xl font-black mb-2 gradient-text`}>
                      {stat.value}
                    </div>
                    <div className={`text-sm md:text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {stat.label}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className={`text-sm md:text-3xl lg:text-4xl font-black mb-4 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Powerful Features for
                <span className="block gradient-text">Modern Fleets</span>
              </h2>
              <p className={`max-w-xl mx-auto text-base ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Everything you need to manage, track, and optimize your fleet operations
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-6 md:gap-8">
              {features.map((feature, index) => (
                <Card
                  key={index}
                  onClick={() => {
                    setActiveFeature(index);
                    if (feature.path) {
                      navigate(feature.path);
                    }
                  }}
                  className={`feature-card cursor-pointer ${
                    theme === 'dark' ? 'glass-dark' : 'glass'
                  } border-0 shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden group ${
                    activeFeature === index ? 'active' : ''
                  }`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = 'https://plus.unsplash.com/premium_photo-1723867341274-da1f98ecd19e?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NTB8fHRyYWNraW5nJTIwZGV2aWNlcyUyMG1hcHxlbnwwfHwwfHx8MA%3D%3D';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                    <div className={`absolute top-3 left-3 w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-lg`}>
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="absolute bottom-3 left-3 right-3">
                      <h3 className={`text-xl font-bold mb-1 text-white`}>
                        {feature.title}
                      </h3>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <p className={`text-sm md:text-sm leading-relaxed ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16 animate-on-scroll">
              <h2 className={`text-sm md:text-3xl lg:text-4xl font-black mb-4 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Why Choose
                <span className="block gradient-text">TrackYourWheelz</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {benefits.map((benefit, index) => (
                <Card
                  key={index}
                  className={`animate-on-scroll ${theme === 'dark' ? 'glass-dark' : 'glass'} border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105 group`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${benefit.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <benefit.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className={`text-xl font-bold mb-3 ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {benefit.title}
                    </h3>
                    <p className={`text-sm md:text-sm leading-relaxed ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Devices Section */}
        <section id="devices" className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16 animate-on-scroll">
              <h2 className={`text-sm md:text-3xl lg:text-4xl font-black mb-4 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Our
                <span className="block gradient-text">Devices</span>
              </h2>
              <p className={`max-w-xl mx-auto text-base ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                State-of-the-art GPS trackers for real-time fleet and asset monitoring
              </p>
            </div>

            <div className="space-y-8 md:space-y-10">
              {devices.map((device, index) => (
                <Card
                  key={device.name}
                  className={`animate-on-scroll overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-500 rounded-2xl md:rounded-3xl p-0 ${
                    theme === 'dark' ? 'glass-dark' : 'glass'
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex flex-col md:flex-row md:min-h-[280px]">
                    {/* Left: Image - touches top and bottom of card */}
                    <div className="relative w-full md:w-2/5 lg:w-2/5 min-h-[220px] md:min-h-full md:flex-shrink-0 overflow-hidden rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none">
                      <img
                        src={device.image}
                        alt={device.name}
                        className="w-full h-full min-h-full object-cover object-center"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1603638725135-928baf863eff?w=800&auto=format&fit=crop&q=80';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent md:bg-gradient-to-r" />
                      
                    </div>
                    {/* Right: Content */}
                    <div className="flex flex-1 flex-col justify-center p-6 md:p-8 lg:p-10 text-left">
                      <h3 className={`text-xl md:text-2xl lg:text-3xl font-bold mb-4 ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {device.name}
                      </h3>
                      <p className={`text-sm md:text-base leading-relaxed mb-6 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {device.description}
                      </p>
                      <div className="mt-auto">
                        <Button
                          variant="ghost"
                          className={`font-semibold transition-all duration-300 hover:scale-105 border-0 ${
                            theme === 'dark'
                              ? 'text-cyan-400 '
                              : 'text-blue-600 '
                          }`}
                          onClick={() => device.learnMorePath ? navigate(device.learnMorePath) : document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                        >
                          Learn more
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16 animate-on-scroll">
              <h2 className={`text-sm md:text-3xl lg:text-4xl font-black mb-4 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Trusted by Industry
                <span className="block gradient-text">Leaders</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <Card
                  key={index}
                  className={`animate-on-scroll ${theme === 'dark' ? 'glass-dark' : 'glass'} border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className={`text-sm leading-relaxed mb-6 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      "{testimonial.content}"
                    </p>
                    <div>
                      <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {testimonial.name}
                      </p>
                      <p className={`text-sm md:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {testimonial.role}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className={`animate-on-scroll ${theme === 'dark' ? 'glass-dark' : 'glass'} border-0 shadow-2xl text-center overflow-hidden relative`}>
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-pink-500/10 animate-shimmer" />
              <CardContent className="p-12 md:p-16 relative z-10">
                <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center animate-float`}>
                  <Zap className="w-10 h-10 text-white" />
                </div>
                <h2 className={`text-sm md:text-3xl lg:text-4xl font-black mb-4 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Ready to Transform Your Fleet?
                </h2>
                <p className={`text-sm  mb-8 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Join thousands of businesses already using TrackYourWheelz to optimize their operations and reduce costs.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={() => navigate('/login')}
                    className={`h-14 px-8 text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-xl ${
                      theme === 'dark'
                        ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] hover:from-[#5a4da3] hover:to-[#3d2f6e] text-white'
                        : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
                    }`}
                  >
                    Start Free Trial
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/about')}
                    className={`h-14 px-8 text-lg font-semibold transition-all duration-300 hover:scale-105 ${
                      theme === 'dark'
                        ? 'border-white/20 hover:bg-white/10 text-white'
                        : 'border-gray-300 hover:bg-gray-100 text-gray-900'
                    }`}
                  >
                    Learn More
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
      <div className="fixed bottom-4 left-0 right-0 px-4 sm:hidden z-40 pointer-events-none">
        <div className="max-w-md mx-auto pointer-events-auto ">
          <Button
            onClick={() => navigate('/login')}
            className={`w-full h-12 text-base font-semibold text-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-full  ${
              theme === 'dark'
                ? 'bg-gradient-to-r from-[#0f1729] via-[#1f2454] to-[#2d1b4e] hover:from-[#172041] hover:via-[#273263] hover:to-[#3f2a72]'
                : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
            }`}
          >
            Log In
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </>
  );
};

export default Home;
